// src/pages/Employees.js
import { useState, useEffect, useContext } from "react";
import { Container, Typography } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import useLanguage from "../../utils/useLanguage";
import { useNavigate } from "react-router";

import ErrorBox from "../../components/ErrorBox";
import Dropdown from "../../components/Dropdown";
import BackToDashboard from "../../components/BackToDashboard";
import { DataContext } from "../../utils/DataContext";
import Loader from "../../components/Loader";
import { UserAuth } from "../../utils/AuthContext";
import AreYouSureModal from "../../components/AreYouSureModal";
import PasswordInput from "../../components/PasswordInput";
import { debounce } from "../../utils/debounce";
import PageWrapper from "../../components/PageWrapper";

export default function UsersPage() {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userModal, setUserModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userFields, setUserFields] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersTotalRecords, setUsersTotalRecords] = useState(0);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const { selectedSupplier, loading, setLoading } = useContext(DataContext);
  const { userData } = useContext(UserAuth);

  const navigate = useNavigate();

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessage2, setErrorMessage2] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const organization = localStorage.getItem("username");
  const organizationId = localStorage.getItem("organizationId");

  const options = [
    { name: t("cuttingManager"), value: "cuttingManager" },
    { name: t("loadingManager"), value: "loadingManager" },
    { name: t("userAdmin"), value: "userAdmin" },
  ];

  const employeeOptions = [
    { name: t("userAdmin"), value: "userAdmin" },
    { name: t("employee"), value: "employee" },
  ];

  const schoolEmployees = [
    { name: t("userAdmin"), value: "userAdmin" },
    { name: t("schoolAdmin"), value: "schoolAdmin" },
    { name: t("employee"), value: "employee" },
    { name: t("teacher"), value: "teacher" },
    { name: t("accountant"), value: "accountant" },
    { name: t("supervisor"), value: "supervisor" },
    { name: t("driver"), value: "driver" },
    { name: t("assistant"), value: "assistant" },
    { name: t("receptionist"), value: "receptionist" },
    { name: t("security"), value: "security" },
    { name: t("cleaner"), value: "cleaner" },
  ];


  const bankEmployees = [
    { name: t("userAdmin"), value: "userAdmin" },
    {
      name: `${t("employee")} (${t("applicationsCreator")})`,
      value: "applicationsCreator",
    },
    {
      name: `${t("employee")} (${t("applicationsAuditor")})`,
      value: "applicationsAuditor",
    },
    {
      name: `${t("employee")} (${t("paymentExecutor")})`,
      value: "paymentExecutor",
    },
  ];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  useEffect(() => {
    const abortController = new AbortController();

    const getEmployees = async () => {
      const token =
        localStorage.getItem("SuperAdminToken") ||
        localStorage.getItem("adminToken");

      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_URL}/getEmployees`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "User-Id": organizationId,
            },
            params: {
              page: usersCurrentPage,
              limit: 5,
              search: debouncedSearchTerm,
            },
            signal: abortController.signal,
          },
        );

        if (response.data && response.data.employees) {
          setUsers(response.data.employees);
          setFilteredUsers(response.data.employees);
          setUsersTotalPages(response.data.totalPages);
          setUsersTotalRecords(response.data.totalRecords);
        } else {
          console.error("Failed to get the users");
        }
        setLoading(false);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          console.log("Request was cancelled");
          return;
        }
        setLoading(false);
        console.error("Failed to get the users", error);
      }
    };

    getEmployees();

    return () => {
      abortController.abort();
    };
  }, [usersCurrentPage, debouncedSearchTerm]);


  const getEmployees = async () => {
    const token =
      localStorage.getItem("SuperAdminToken") ||
      localStorage.getItem("adminToken");

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getEmployees`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": organizationId,
          },
          params: {
            page: usersCurrentPage,
            limit: 5,
            search: searchTerm,
          },
        },
      );

      if (response.data && response.data.employees) {
        setUsers(response.data.employees);
        setFilteredUsers(response.data.employees);
        setUsersTotalPages(response.data.totalPages);
        setUsersTotalRecords(response.data.totalRecords);
      } else {
        console.error("Failed to get the users");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Failed to get the users", error);
    }
  };

  const openConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const openUserModal = (user) => {
    setSelectedUser(user);
    setUserFields({
      userName: user.username,
      userEmail: user.email,
      userPhoneNumber: user.phoneNumber,
      organizationName:
        user.organization?.organizationName || "No organization",
      isBlocked: user.isBlocked,
      role: user.role,
    });
    setIsEditing(false);
    setUserModal(true);
  };

  const handleInputChange = (e, fieldName) => {
    const value = fieldName === "isBlocked" ? e.target.checked : e.target.value;
    setUserFields({
      ...userFields,
      [fieldName]: value,
    });
  };

  const handleSaveUser = async (user) => {
    try {
      setLoadingModal(true);
      const response = await axios.patch(
        `${process.env.REACT_APP_URL}/editUser`,
        {
          id: selectedUser?.id,
          username: userFields.userName,
          email: userFields.userEmail,
          phoneNumber: userFields.userPhoneNumber,
          isBlocked: userFields.isBlocked,
          role: userFields.role,
        },
      );

      if (response.status === 200) {
        setUserModal(false);
      } else {
        console.error("Failed to update user:", response.data.message);
      }
      setLoadingModal(false);
    } catch (error) {
      console.error("Error updating user:", error);
      setLoadingModal(false);
    } finally {
      await getEmployees();
    }
  };

  const handleCancel = () => {
    setRemoveUserModal(false);
    setUserModal(false);
    setSelectedUser(null);
    setLoadingModal(false);
  };

  const openRemoveUserModal = (user) => {
    setSelectedUser(user);
    setRemoveUserModal(true);
  };

  const [showRemoveUserModal, setRemoveUserModal] = useState(false);
  const handleDeleteUser = async () => {
    const token = localStorage.getItem("token");
    setLoadingModal(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_URL}/deleteUser/${selectedUser.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.message === "User deleted successfully") {
        setRemoveUserModal(false);
        setUserModal(false);
      } else {
        console.error("Failed to delete user:", response.data.message);
      }
      setRemoveUserModal(false);
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setLoadingModal(false);
      setUserModal(false);
      await getEmployees();
    }
  };

  const [message, setMessage] = useState("");
  const [addUserModal, setAddUserModal] = useState(false);
  const [newUserFields, setNewUserFields] = useState({
    userName: "",
    userEmail: "",
    password: "",
    confirmPassword: "",
    isBlocked: false,
    role: "",
  });

  const openAddUserModal = () => {
    setAddUserModal(true);
    setNewUserFields({
      userName: "",
      userEmail: "",
      password: "",
      confirmPassword: "",
      isSuperUser: false,
      role: "",
    });
  };

  const handleNewUserInputChange = (e, fieldName) => {
    const value = fieldName === "isBlocked" ? e.target.checked : e.target.value;
    setNewUserFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      const language = currentDirection === "rtl" ? "ar" : "en";

      if (
        !newUserFields.userEmail ||
        !newUserFields.userName ||
        !newUserFields.password ||
        !newUserFields.confirmPassword ||
        !newUserFields.phoneNumber
      ) {
        setError(true);
        setErrorMessage(t("emptyInputFields"));
        return;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (newUserFields.userEmail) {
        if (!emailPattern.test(newUserFields.userEmail)) {
          setError(true);
          setErrorMessage(t("invalidEmail"));
          return;
        }
      }

      if (newUserFields.password !== newUserFields.confirmPassword) {
        setError(true);
        setErrorMessage("Passwords do not match");
        return;
      }

      const orgType = localStorage.getItem("organizationType");

      setLoadingModal(true);
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createUser`,
        {
          email: newUserFields.userEmail,
          username: newUserFields.userName,
          password: newUserFields.password,
          phoneNumber: newUserFields.phoneNumber,
          isBlocked: newUserFields.isBlocked,
          isOrganization: true,
          organizationId: Number(organizationId),
          language,
          role:
            orgType == "PAPERFACTORY" && newUserFields.role === ""
              ? ""
              : newUserFields.role,
        },
      );

      setMessage(response.data.message);
      if (response.data.message === "User created successfully") {
        setError(false);
        setErrorMessage("");
        setAddUserModal(false);
      }
      setError(false);
      setErrorMessage("");
      setAddUserModal(false);
      setLoading(false);
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        const errorKey = error.response.data.error;
        switch (errorKey) {
          case "emailAlreadyInUse":
            setError(true);
            setErrorMessage(t("emailAlreadyInUse"));
            break;
          case "invalidPhoneNumber":
            setError(true);
            setErrorMessage(t("invalidPhoneNumber"));
            break;
          case "reachedLimitOfSubscription":
            setError(true);
            setErrorMessage(t("reachedLimitOfSubscription"));
            setErrorMessage2(t(error.response.data.subscriptionType));
            break;
          default:
            setError(true);
            setErrorMessage(t("unexpectedError"));
            break;
        }
      } else {
        console.error("Network or other error:", error.message);
        setError(true);
        setErrorMessage(t("networkError"));
      }
    } finally {
      setLoadingModal(false);
      await getEmployees();
    }
  };

  const handleCancelAddUser = () => {
    setError(false);
    setErrorMessage("");
    setShowConfirmationModal(false);
    setAddUserModal(false);
  };

  return (
    <div className="e-container">
      <PageWrapper
          style={{ direction: currentDirection }}


      >


        <div className="users-title-section">
          <h4 className="page--title">
            {t("users", { defaultValue: "users" })}
          </h4>

          {userData.userType === "organization" ? (
            <CustomButton
              title={t("createUser")}
              variant="blank"
              icon="addP"
              iconSize={20}
              onClick={openAddUserModal}
            />
          ) : null}
        </div>

        <CustomInput
          name="userName"
          value={searchTerm}
          placeholder={searchTerm ? "" : t("search")}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setUsersCurrentPage(1);
          }}
        />

        {loading ? (
          <Loader />
        ) : (
          <>
            {filteredUsers.length === 0 ? (
              <p>{t("noUsers")}</p>
            ) : (
              <>
                <ul
                  className="users-list"
                  style={{ direction: currentDirection }}
                >
                  {filteredUsers.map((user) => (
                    <li
                      className="user-list-item"
                      key={user.id}
                      onClick={() => openUserModal(user)}
                      style={{ cursor: "pointer" }}
                    >
                      <p className="user-username">
                        {user.username || "No Name Available"}
                      </p>
                      {user.email && (
                        <Typography variant="subtitle1" color="textSecondary">
                          {user.email}
                        </Typography>
                      )}
                    </li>
                  ))}
                </ul>

                <section className="pagination">
                  <div className="pagination-buttons">
                    <CustomButton
                      variant="blank"
                      title={t("previous")}
                      onClick={() => setUsersCurrentPage(usersCurrentPage - 1)}
                      disabled={usersCurrentPage === 1}
                      icon={currentDirection === "ltr" ? "previous" : "next"}
                      iconSize={15}
                    />
                    <span className="pagination-location">
                      {t("page")}: {usersCurrentPage} - {usersTotalPages}
                    </span>
                    <CustomButton
                      title={t("next")}
                      variant="blank"
                      onClick={() => setUsersCurrentPage(usersCurrentPage + 1)}
                      disabled={usersCurrentPage === usersTotalPages}
                      icon={currentDirection === "ltr" ? "next" : "previous"}
                      iconSize={15}
                      reverseIcon
                    />
                  </div>

                  <div className="pagination-buttons">
                    <span className="pagination-location">
                      {t("totalNumber")}: {usersTotalRecords}
                    </span>
                  </div>
                </section>
              </>
            )}
          </>
        )}

        {userModal && (
          <Modal
            title={isEditing ? t("editUser") : t("user")}
            onClose={handleCancel}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="edit-user-actions">
                  {isEditing ? (
                    <>
                      <CustomButton
                        title={t("confirm")}
                        onClick={() => handleSaveUser(selectedUser)}
                        icon="save"
                        iconSize={16}
                        disabled={loadingModal}
                      />
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleCancel}
                        variant="blank"
                        icon="close"
                        iconSize={14}
                      />
                    </>
                  ) : (
                    <>
                      <CustomButton
                        variant="blank"
                        title={t("edit")}
                        onClick={() => setIsEditing(true)}
                        icon="edit"
                        iconSize={18}
                      />
                      <CustomButton
                        title={t("delete")}
                        variant="red"
                        icon="delete"
                        iconSize={18}
                        onClick={() => openRemoveUserModal(selectedUser)}
                      />
                    </>
                  )}
                </section>
              </>
            }
          >
            <div>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("username")}: </span>
                      <CustomInput
                        name="userName"
                        value={userFields.userName || ""}
                        onChange={(e) => handleInputChange(e, "userName")}
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("username")}:</p>
                      <p>{selectedUser?.username}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("userRole")}: </span>
                      <Dropdown
                        options={
                          userData?.organizationType === "PAPERFACTORY"
                            ? options
                            : userData?.organizationType === "BANK"
                              ? bankEmployees
                              : employeeOptions
                        }
                        setSelected={(item) => {
                          setUserFields((prev) => {
                            return {
                              ...prev,
                              role: item.value,
                            };
                          });
                        }}
                        flex
                        thick
                        selected={{ title: userFields?.role }}
                        value={selectedUser?.role}
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("userRole")}:</p>
                      <p>{t(selectedUser?.role)}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                <div className="view-user-entity">
                  <p>{t("email")}:</p>
                  <p>{selectedUser?.email}</p>
                </div>
              </section>
              <section>
                <div>
                  <div className="view-user-entity">
                    <p>{t("organization")}:</p>
                    <p>{organization || "Individual user"}</p>
                  </div>
                </div>
              </section>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>{t("phoneNumber")}: </span>
                      <CustomInput
                        name="userPhoneNumber"
                        value={userFields.userPhoneNumber || ""}
                        onChange={(e) =>
                          handleInputChange(e, "userPhoneNumber")
                        }
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("phoneNumber")}:</p>
                      <p>{selectedUser?.phoneNumber}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                {isEditing ? (
                  <div className="edit-user-entity">
                    <div className="user-row-section">
                      <CustomCheckbox
                        checked={userFields?.isBlocked}
                        onChange={(e) => handleInputChange(e, "isBlocked")}
                        color="primary"
                      />
                      <label>{t("isBlocked")}</label>
                    </div>
                  </div>
                ) : (
                  <div className="view-user-entity">
                    <p>{t("theUseIsBlocked")}:</p>
                    <p>{selectedUser?.isBlocked ? t("yes") : t("no")}</p>
                  </div>
                )}
              </section>
            </div>
          </Modal>
        )}

        {showRemoveUserModal && (
          <Modal
            title={t("deleteUser")}
            onClose={() => setRemoveUserModal(false)}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="delete-user-actions">
                  <CustomButton
                    onClick={handleDeleteUser}
                    title={t("confirm")}
                    icon="save"
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={() => setRemoveUserModal(false)}
                    variant="blank"
                    icon="close"
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <div className="delete-modal-body">
              <h3>{t("areYouSureYouWantToDeleteThisUser")}</h3>
              <p>{selectedUser?.username || selectedUser?.email}</p>
            </div>
          </Modal>
        )}

        {addUserModal && (
          <Modal
            title={t("createUser")}
            onClose={openConfirmationModal}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="edit-user-actions">
                  <CustomButton
                    title={t("confirm")}
                    onClick={handleCreateUser}
                    icon="save"
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={openConfirmationModal}
                    variant="blank"
                    icon="close"
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <form onSubmit={handleCreateUser}>
              <div>
                {error && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <ErrorBox>
                      <div className="o-column">
                        <span className="t-text-align_center">
                          {errorMessage}
                        </span>
                        {errorMessage2 && (
                          <p className="t-text-align_center">
                            {t("currentPlan")}:
                            <span className="t-bold_bold ">
                              {errorMessage2}
                            </span>
                          </p>
                        )}
                      </div>
                    </ErrorBox>
                  </div>
                )}
                <div className="edit-user-entity">
                  <span>{t("username")}: </span>
                  <CustomInput
                    name="userName"
                    value={newUserFields.userName}
                    onChange={(e) => handleNewUserInputChange(e, "userName")}
                  />
                </div>

                <div className="edit-user-entity">
                  <span>{t("userRole")}: </span>
                  <Dropdown
                    options={
                      userData?.organizationType === "PAPERFACTORY"
                        ? options
                        : userData?.organizationType === "BANK" ? bankEmployees : userData?.organizationType === "SCHOOL" ? schoolEmployees : employeeOptions
                    }
                    setSelected={(item) => {
                      setNewUserFields((prev) => {
                        return { ...prev, role: item.value };
                      });
                    }}
                    flex
                    thick
                    selected={{ title: newUserFields?.role }}
                    value={newUserFields?.role}
                  />
                </div>

                <div className="edit-user-entity">
                  <span>{t("email")}: </span>
                  <CustomInput
                    name="userEmail"
                    type="email"
                    value={newUserFields.userEmail}
                    onChange={(e) => handleNewUserInputChange(e, "userEmail")}
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("organization")}: </span>
                  <CustomInput
                    name="organizationName"
                    value={organization}
                    disabled
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("phoneNumber")}: </span>
                  <CustomInput
                    name="userName"
                    value={newUserFields.phoneNumber}
                    onChange={(e) => handleNewUserInputChange(e, "phoneNumber")}
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("password")}: </span>
                  <PasswordInput
                    label={t("password")}
                    placeholder={t("password")}
                    value={newUserFields.password}
                    onChange={(e) => handleNewUserInputChange(e, "password")}
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <span>{t("confirmPassword")}: </span>
                  <PasswordInput
                    label={t("confirmPassword")}
                    placeholder={t("confirmPassword")}
                    value={newUserFields.confirmPassword}
                    onChange={(e) =>
                      handleNewUserInputChange(e, "confirmPassword")
                    }
                    required
                  />
                </div>
                <div className="edit-user-entity">
                  <div className="user-row-section">
                    <CustomCheckbox
                      checked={newUserFields.isBlocked}
                      onChange={(e) => handleNewUserInputChange(e, "isBlocked")}
                      color="primary"
                    />

                    <label>{t("isBlocked")}</label>
                  </div>
                </div>
              </div>
            </form>
          </Modal>
        )}
        {showConfirmationModal && (
          <AreYouSureModal
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={handleCancelAddUser}
            confirmIconType="whiteCheck"
            customMessage={"areYouSureDefaultMessage"}
            confirmTitle="confirm"
          />
        )}
      </PageWrapper>
    </div>
  );
}
